import React, { useState } from 'react'
import { View, Text } from 'react-native'
import { Link, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { apiPost } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../components/ui/KeyboardAwareScrollView'
import { useToast } from '../../components/ui/Toast'
import { ScreenHeader } from '../../components/ui/ScreenHeader'
import { goBackOr } from '../../lib/goBack'
import type { ForgotPasswordRequest } from '@warcarp/api-types'

export default function ForgotPasswordScreen() {
  const router = useRouter()
  const { showToast } = useToast()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  async function handleSubmit() {
    if (!email.trim()) return
    setLoading(true)
    try {
      await apiPost<void>('/api/v1/auth/forgot-password', { email: email.trim() } satisfies ForgotPasswordRequest)
      setSent(true)
    } catch {
      showToast('Something went wrong. Please try again.', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <ScreenHeader title="Reset Password" icon="lock-reset" onBack={() => goBackOr(router, '/(auth)/login')} />
      {sent ? (
        <View className="flex-1 bg-canvas justify-center px-6 gap-6">
          <View className="gap-3">
            <Text className="text-4xl">✉️</Text>
            <Text className="text-ink text-2xl font-stencil uppercase">Check your email</Text>
            <Text className="text-ink-soft font-typed">
              If an account exists for{' '}
              <Text className="text-ink font-typed">{email}</Text>
              , you'll receive a reset link shortly.
            </Text>
          </View>
          <Link href="/(auth)/login" className="text-blood font-semibold text-base">
            Back to Sign In
          </Link>
        </View>
      ) : (
        <KeyboardAwareScrollView
          className="flex-1 bg-canvas"
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 24, gap: 32 }}
        >
          <View className="gap-2">
            <Text className="text-ink-soft font-typed">Enter your email to receive a reset link.</Text>
          </View>
          <Input
            label="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoComplete="email"
            returnKeyType="go"
            // Enter / Return submits, like an HTML form on desktop.
            onSubmitEditing={() => { if (!loading) void handleSubmit() }}
          />
          <Button title="Send Reset Link" onPress={handleSubmit} loading={loading} disabled={!email.trim()} />
        </KeyboardAwareScrollView>
      )}
    </SafeAreaView>
  )
}
